import { useEffect, useMemo, useState, useCallback, useRef, Fragment } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, ListTodo, Plus, Clock, MapPin, Trash2, Pencil, Loader2, User, CalendarPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { googleCalendarUrl } from "@/lib/gcal";
import { APPT_KINDS } from "@/lib/apptKinds";

const dayKey = (d) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const fmtDateLong = (d) => d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const PRIO = { high: { label: "High", color: "#ef4444" }, normal: { label: "Normal", color: "#0ea5e9" }, low: { label: "Low", color: "#64748b" } };

function CalendarTab() {
  const [appts, setAppts] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/appointments").then(({ data }) => setAppts(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    appts.forEach((a) => { const k = dayKey(a.start); (m[k] = m[k] || []).push(a); });
    return m;
  }, [appts]);
  const dayList = (byDay[dayKey(selected)] || []).slice().sort((a, b) => (a.start > b.start ? 1 : -1));

  const del = async (a) => { if (!window.confirm(`Delete "${a.title}"?`)) return; try { await api.delete(`/appointments/${a.appointment_id}`); toast.success("Appointment deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const draggedRef = useRef(null);
  const reschedule = async (appt, date) => {
    const timePart = (appt.start || "").slice(11, 16) || "09:00";
    const newStart = `${dayKey(date)}T${timePart}`;
    if (newStart.slice(0, 10) === (appt.start || "").slice(0, 10)) return; // same day, no-op
    const newEnd = appt.end ? `${dayKey(date)}T${appt.end.slice(11, 16)}` : null;
    try {
      await api.patch(`/appointments/${appt.appointment_id}`, {
        title: appt.title, start: newStart, start_at: new Date(newStart).toISOString(),
        end: newEnd, location: appt.location || "", notes: appt.notes || "", kind: appt.kind || "call",
        lead_id: appt.lead_id || null, lead_name: appt.lead_name || null,
      });
      toast.success(`Moved to ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`);
      setSelected(date); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7am–8pm
  const pad2 = (n) => String(n).padStart(2, "0");
  const fmtLocal = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}T${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
  const rescheduleTo = async (appt, date, hour) => {
    const mins = parseInt((appt.start || "").slice(14, 16) || "0", 10);
    const start = new Date(date); start.setHours(hour, mins, 0, 0);
    const newStart = fmtLocal(start);
    let newEnd = null;
    if (appt.end && appt.start) {
      const dur = new Date(appt.end).getTime() - new Date(appt.start).getTime();
      if (dur > 0) newEnd = fmtLocal(new Date(start.getTime() + dur));
    }
    try {
      await api.patch(`/appointments/${appt.appointment_id}`, {
        title: appt.title, start: newStart, start_at: new Date(newStart).toISOString(),
        end: newEnd, location: appt.location || "", notes: appt.notes || "", kind: appt.kind || "call",
        lead_id: appt.lead_id || null, lead_name: appt.lead_name || null,
      });
      toast.success(`Moved to ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${fmtTime(newStart)}`);
      setSelected(date); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const DayContent = ({ date }) => {
    const kinds = [...new Set((byDay[dayKey(date)] || []).map((a) => a.kind || "call"))].slice(0, 3);
    return (
      <div className="relative flex h-full w-full items-center justify-center"
        onDragOver={(e) => { if (draggedRef.current) e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); const a = draggedRef.current; draggedRef.current = null; if (a) reschedule(a, date); }}>
        {date.getDate()}
        {kinds.length > 0 && (
          <span className="absolute -bottom-1 left-1/2 flex -translate-x-1/2 gap-0.5">
            {kinds.map((k, i) => <span key={i} className="h-1 w-1 rounded-full" style={{ backgroundColor: (APPT_KINDS[k] || APPT_KINDS.call).color }} />)}
          </span>
        )}
      </div>
    );
  };

  const [view, setView] = useState("month");
  const weekDays = useMemo(() => {
    const base = new Date(selected); const dow = base.getDay();
    const sun = new Date(base); sun.setDate(base.getDate() - dow);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(sun); d.setDate(sun.getDate() + i); return d; });
  }, [selected]);
  const shiftWeek = (n) => { const d = new Date(selected); d.setDate(d.getDate() + n * 7); setSelected(d); };

  return (
    <div className="space-y-4">
      <div className="flex gap-2" data-testid="planner-view-toggle">
        <button onClick={() => setView("month")} data-testid="view-month" className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${view === "month" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}>Month</button>
        <button onClick={() => setView("week")} data-testid="view-week" className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${view === "week" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}>Week</button>
      </div>
      {view === "month" ? (
      <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
      <Card className="p-4" data-testid="planner-calendar-card">
        <Calendar mode="single" selected={selected} onSelect={(d) => d && setSelected(d)} components={{ DayContent }} />
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground" data-testid="appt-legend">
          {Object.entries(APPT_KINDS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: v.color }} /> {v.label}</span>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Tip: drag an appointment onto a day to reschedule it.</p>
      </Card>
      <Card className="p-5" data-testid="planner-day-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-semibold">{fmtDateLong(selected)}</h3>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-appointment-button"><Plus className="mr-1.5 h-4 w-4" /> Add appointment</Button>
        </div>
        <div className="mt-4 space-y-2" data-testid="day-appointments">
          {dayList.length === 0 && <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="day-empty">Nothing scheduled. Add your first appointment for this day.</p>}
          {dayList.map((a) => (
            <div key={a.appointment_id} draggable onDragStart={() => { draggedRef.current = a; }} onDragEnd={() => { draggedRef.current = null; }}
              className="flex cursor-move items-start justify-between gap-3 rounded-lg border border-l-4 border-border p-3" style={{ borderLeftColor: (APPT_KINDS[a.kind] || APPT_KINDS.call).color }} data-testid={`appointment-${a.appointment_id}`}>
              <div className="min-w-0">
                <p className="font-medium">{a.title}</p>
                {a.lead_name && <p className="mt-0.5 flex items-center gap-1 text-xs text-primary" data-testid={`appt-lead-${a.appointment_id}`}><User className="h-3 w-3" /> {a.lead_name}</p>}
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(a.start)}{a.end ? ` – ${fmtTime(a.end)}` : ""}</span>
                  {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                </p>
                {a.notes && <p className="mt-1 text-xs text-muted-foreground">{a.notes}</p>}
              </div>
              <div className="flex shrink-0 gap-1">
                {googleCalendarUrl(a) && (
                  <a href={googleCalendarUrl(a)} target="_blank" rel="noopener noreferrer" title="Add to Google Calendar"
                    data-testid={`gcal-appointment-${a.appointment_id}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                    <CalendarPlus className="h-4 w-4" />
                  </a>
                )}
                <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`edit-appointment-${a.appointment_id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => del(a)} data-testid={`delete-appointment-${a.appointment_id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
      </div>
      ) : (
      <Card className="p-4" data-testid="planner-week-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => shiftWeek(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium">{weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – {weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            <Button size="icon" variant="ghost" onClick={() => shiftWeek(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-appointment-button-week"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
        </div>
        <div className="mt-3 overflow-x-auto" data-testid="week-grid">
          <div className="grid min-w-[720px]" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
            <div />
            {weekDays.map((d) => {
              const isToday = dayKey(d) === dayKey(new Date());
              return <div key={dayKey(d)} className={`px-1 pb-2 text-center text-xs font-semibold ${isToday ? "text-primary" : "text-muted-foreground"}`} data-testid={`week-col-${dayKey(d)}`}>{d.toLocaleDateString("en-US", { weekday: "short" })} {d.getDate()}</div>;
            })}
            {HOURS.map((h) => (
              <Fragment key={h}>
                <div className="border-t border-border py-1 pr-1 text-right text-[10px] text-muted-foreground">{(h % 12) || 12}{h < 12 ? "a" : "p"}</div>
                {weekDays.map((d) => {
                  const cell = (byDay[dayKey(d)] || []).filter((a) => {
                    const hh = parseInt((a.start || "").slice(11, 13) || "0", 10);
                    return hh === h || (h === HOURS[0] && hh < HOURS[0]) || (h === HOURS[HOURS.length - 1] && hh > HOURS[HOURS.length - 1]);
                  });
                  return (
                    <div key={dayKey(d) + h} onDragOver={(e) => { if (draggedRef.current) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); const a = draggedRef.current; draggedRef.current = null; if (a) rescheduleTo(a, d, h); }}
                      className="min-h-[38px] border-l border-t border-border p-0.5" data-testid={`week-cell-${dayKey(d)}-${h}`}>
                      {cell.map((a) => (
                        <button key={a.appointment_id} draggable onDragStart={() => { draggedRef.current = a; }} onDragEnd={() => { draggedRef.current = null; }} onClick={() => { setEditing(a); setDialogOpen(true); }}
                          className="mb-0.5 flex w-full cursor-move items-center gap-1 rounded border-l-2 bg-accent/50 px-1 py-0.5 text-left text-[10px] hover:bg-accent" style={{ borderLeftColor: (APPT_KINDS[a.kind] || APPT_KINDS.call).color }} data-testid={`week-appt-${a.appointment_id}`}>
                          <span className="truncate">{fmtTime(a.start)} {a.title}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      </Card>
      )}
      <AppointmentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} initial={editing} defaultDate={selected} />
    </div>
  );
}

function TodoTab() {
  const [todos, setTodos] = useState([]);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("normal");
  const [due, setDue] = useState("");
  const [filter, setFilter] = useState("active");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/todos").then(({ data }) => setTodos(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return toast.error("Enter a task");
    setBusy(true);
    try { await api.post("/todos", { title, priority, due_date: due || null }); setTitle(""); setDue(""); setPriority("normal"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggle = async (t) => { try { await api.patch(`/todos/${t.todo_id}`, { done: !t.done }); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const del = async (t) => { try { await api.delete(`/todos/${t.todo_id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const shown = todos.filter((t) => filter === "all" ? true : filter === "done" ? t.done : !t.done);
  const activeCount = todos.filter((t) => !t.done).length;

  return (
    <div className="space-y-5">
      <Card className="p-4" data-testid="todo-add-card">
        <div className="grid gap-3 md:grid-cols-[1fr_150px_170px_auto] md:items-end">
          <div><Label>New task</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="mt-1.5" placeholder="Follow up with…" data-testid="todo-title-input" /></div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1.5" data-testid="todo-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["high", "normal", "low"].map((p) => <SelectItem key={p} value={p}>{PRIO[p].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Due <span className="font-normal text-muted-foreground">(optional)</span></Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5" data-testid="todo-due-input" /></div>
          <Button onClick={add} disabled={busy} data-testid="todo-add-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Add</Button>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2" data-testid="todo-filters">
        {[["active", `Active (${activeCount})`], ["all", "All"], ["done", "Completed"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`todo-filter-${id}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${filter === id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}>{lbl}</button>
        ))}
      </div>

      <Card className="divide-y divide-border/60" data-testid="todo-list">
        {shown.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="todo-empty">No tasks here. Add one above to get started.</p>}
        {shown.map((t) => {
          const p = PRIO[t.priority] || PRIO.normal;
          return (
            <div key={t.todo_id} className="flex items-center gap-3 p-3.5" data-testid={`todo-${t.todo_id}`}>
              <Checkbox checked={t.done} onCheckedChange={() => toggle(t)} data-testid={`todo-toggle-${t.todo_id}`} />
              <div className="min-w-0 flex-1">
                <p className={`truncate text-sm font-medium ${t.done ? "text-muted-foreground line-through" : ""}`}>{t.title}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5" style={{ backgroundColor: p.color + "22", color: p.color }}>{p.label}</span>
                  {t.due_date && <span>Due {new Date(t.due_date + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                </div>
              </div>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => del(t)} data-testid={`delete-todo-${t.todo_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default function Planner() {
  const [tab, setTab] = useState("calendar");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Planner</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your appointments and to-do list, all in one place.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab("calendar")} data-testid="planner-tab-calendar"
          className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === "calendar" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}><CalendarDays className="h-4 w-4" /> Calendar</button>
        <button onClick={() => setTab("todo")} data-testid="planner-tab-todo"
          className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === "todo" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}><ListTodo className="h-4 w-4" /> To-Do</button>
      </div>
      {tab === "calendar" ? <CalendarTab /> : <TodoTab />}
    </div>
  );
}
